import axios, {AxiosError} from 'axios';

import {type PrintsData, ProductData} from '@/redux/reducers/productReducer';
import type {CurrencyType} from '@/redux/reducers/typings';
import type {
  ArtaSupportedCurrency,
  ArtaQuoteRequest,
  ArtaHostedSession,
  GetQuoteInput,
  GetSessionInput,
  ArtaShippingEstimate,
  ArtaLocation,
  ArtaObject,
  ArtaQuoteType,
} from './typings';

import {parseDimensionsFromString} from '@/hooks/use-products-by-slugs/utils';
import countries from './countries-summary.json';

const ARTA_BASE_URL = 'https://api.arta.io';
const ARTA_PREFIX = 'ARTA_APIKey';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * @description Extracts a meaningful error message from various error types.
 * @param {object} param0 - The parameters object.
 * @param {unknown} param0.error - The error to extract the message from.
 * @param {string} param0.defaultMessage - The default message to return if no specific message is found.
 * @return {string} - The extracted error message or the default message.
 */
export const getArtaErrorMessage = ({
  error,
  defaultMessage,
}: {
  error: unknown;
  defaultMessage: string;
}) => {
  if (error instanceof AxiosError) {
    return error.message;
  } else if (error instanceof Error) {
    return error.message;
  }
  /** IF ARTA ERROR */
  if (
    typeof error === 'object' &&
    !!error &&
    'errors' in error &&
    typeof error.errors === 'object' &&
    !!error.errors
  ) {
    return Object.values(error.errors).flat().join(', ');
  }
  return defaultMessage;
};

/**
 * @description Fetches a hosted session from the Arta API,
 * that is required to create shipping estimates.
 * @param {object} params - The parameters object.
 * @param {string} param.origin - The origin location.
 * @param {Array} param.objects - The list of product objects to include in the session.
 * @param {'CAD' | 'EUR' | 'GBP' | 'HKD' | 'USD'} param.currency - The arta supported currency for the session.
 * @returns {Promise<{data: ArtaHostedSession; error: null} | {data: null; error: string}>} - The result of the API call.
 */
export const fetchArtaSession = async ({
  origin,
  objects,
  currency,
}: GetSessionInput): Promise<
  {data: ArtaHostedSession; error: null} | {data: null; error: string}
> => {
  try {
    if (!process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY) {
      throw new Error('Arta API Publishable Key is not defined');
    }
    const {data} = await axios<ArtaHostedSession>({
      method: 'POST',
      url: `${ARTA_BASE_URL}/estimate/hosted_sessions`,
      headers: {
        authorization: `${ARTA_PREFIX} ${process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY}`,
        'cache-control': 'no-cache',
        'content-type': 'application/json',
      },
      data: {
        hosted_session: {
          origin,
          objects,
          currency,
        },
      },
    });

    return {data, error: null};
  } catch (err) {
    return {
      data: null,
      error: getArtaErrorMessage({error: err, defaultMessage: 'Failed to fetch Arta session'}),
    };
  }
};

/**
 * @description Creates and fetches a shipping estimate from the Arta API using a hosted session.
 * @param {object} params - The parameters object.
 * @param {string} params.origin - The origin location.
 * @param {Array} params.objects - The list of product objects to include in the estimate.
 * @param {'CAD' | 'EUR' | 'GBP' | 'HKD' | 'USD'} params.currency - The arta supported currency for the estimate.
 * @param {object} params.destination - The destination location.
 * @param {object} params.session - The hosted session object with id and token, obtained from `fetchArtaSession`.
 * @param {'parcel' | 'premium' | 'select' | 'self_ship'[] | undefined } params.quoteTypes - Optional array of quote types to quote estimates.
 *
 * @returns {Promise<{data: ArtaQuoteRequest; error: null} | {data: null; error: string}>} - The result of the API call.
 */
export const fetchArtaEstimate = async ({
  origin,
  objects,
  currency,
  destination,
  session,
  quoteTypes,
}: GetQuoteInput): Promise<{data: ArtaQuoteRequest; error: null} | {data: null; error: string}> => {
  try {
    if (!process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY) {
      throw new Error('Arta API Publishable Key is not defined');
    }
    const {data} = await axios<ArtaQuoteRequest>({
      method: 'POST',
      url: `${ARTA_BASE_URL}/estimate/requests`,
      headers: {
        authorization: `${ARTA_PREFIX} ${process.env.NEXT_PUBLIC_ARTA_API_PUBLISHABLE_KEY}`,
        'hosted-session-id': session.id.toString(),
        'hosted-session-private-token': session.token,
        'cache-control': 'no-cache',
        'content-type': 'application/json',
      },
      data: {
        request: {
          origin,
          objects,
          currency,
          destination,
          preferred_quote_types: quoteTypes,
        },
      },
    });

    /**
     * @description handle cases where no quotes are returned or status is
     *  not 'quoted' and not 'disqualified'
     * - other statuses: no quotes available
     */
    if (data.status !== 'quoted' && data.status !== 'disqualified') {
      return {data: null, error: 'Failed to fetch Arta estimate'};
    }

    return {data, error: null};
  } catch (err) {
    return {
      data: null,
      error: getArtaErrorMessage({error: err, defaultMessage: 'Failed to fetch Arta estimate'}),
    };
  }
};

/**
 * @description Extracts the necessary parameters from a hosted session object.
 * @param session - The hosted session object.
 * @returns An object containing the id and token of the hosted session.
 */
export const getHostedSessionParams = (session: ArtaHostedSession) => {
  return {
    id: session.id,
    token: session.private_token,
  };
};

/**
 * @description Utility functions for handling Unix timestamps.
 */
export const unixTimestamp = {
  /**
   * @description Converts a Date object to a Unix timestamp.
   * @param {Date|undefined} date - Date object to convert to Unix timestamp.
   * If not provided, current date is used.
   * @returns Unix timestamp in seconds.
   */
  get: (date?: Date) => Math.floor((date ? date.getTime() : Date.now()) / 1000),
  /**
   * @description Checks if a given Unix timestamp has expired based on the specified expiry duration.
   * @param timestamp - Unix timestamp in seconds or any timestamp in milliseconds.
   * @param expiry - Expiry duration in milliseconds (default is 7 days).
   * @returns `true` if the timestamp has expired, `false` otherwise.
   */
  isExpired: (timestamp: number, expiry = SEVEN_DAYS_MS): boolean => {
    const now = Date.now();
    /**
     * check if unix timestamp or not,
     * - convert to milliseconds if needed
     */
    const timestampMs = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return now - timestampMs > expiry;
  },
};

/**
 * @description Extracts a summary of the location details.
 * @param {object} artaLocation - The Arta location large object.
 * @returns {object} A summary object with the brief location details.
 */
const getLocationSummary = ({
  country,
  region,
  city,
  postal_code,
}: ArtaLocation): Pick<ArtaLocation, 'country' | 'region' | 'city' | 'postal_code'> => ({
  country,
  region,
  city,
  postal_code,
});

export const getPricesRange = (prices: number[]): ArtaShippingEstimate['shipping_price'] => {
  if (prices.length === 0) {
    return {min: null, max: null};
  }
  if (prices.length === 1) {
    return {min: prices[0], max: null};
  }

  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  if (minPrice === maxPrice) {
    return {min: minPrice, max: null};
  }
  return {min: minPrice, max: maxPrice};
};

export const getRelevantDisqualifications = ({
  requestedQuoteTypes,
  disqualifications,
}: {
  requestedQuoteTypes?: ArtaQuoteType[];
  disqualifications?: ArtaQuoteRequest['disqualifications'];
}): {
  type: ArtaQuoteType;
  message: string;
}[] => {
  if (!disqualifications || disqualifications.length === 0) {
    return [];
  }
  const quoteTypes: ArtaQuoteType[] = requestedQuoteTypes?.length
    ? requestedQuoteTypes
    : ['parcel', 'premium', 'select', 'self_ship'];
  const requestedSet = new Set(quoteTypes);
  // map quote type -> collected reasons
  const map = new Map<ArtaQuoteType, string[]>();

  disqualifications
    .flatMap((current) =>
      current.quote_types.map((quoteType) => ({quoteType, reason: current.reason})),
    )
    .forEach(({quoteType, reason}) => {
      if (requestedSet.has(quoteType)) {
        if (!map.has(quoteType)) map.set(quoteType, []);
        map.get(quoteType)!.push(reason);
      }
    });

  // build output array
  return quoteTypes
    .filter((quoteType) => map.has(quoteType))
    .map((quoteType) => ({
      type: quoteType,
      message: map.get(quoteType)!.join('. '),
    }));
};

/**
 * @description Extracts the shipping estimate from the Arta quote large quote request object.
 * @param {object} artaQuoteRequest - The Arta quote request object.
 * @param {string} productSlug - The product slug.
 * @returns {object} The shipping estimate object to fit our needs.
 */
export const getShippingEstimate = (
  artaQuoteRequest: ArtaQuoteRequest,
  productSlug: string,
  quoteTypes?: ArtaQuoteType[],
): ArtaShippingEstimate => {
  const from = getLocationSummary(artaQuoteRequest.origin);
  const to = getLocationSummary(artaQuoteRequest.destination);
  const quotesPrices = artaQuoteRequest.quotes.map((quote) => quote.total);

  return {
    from,
    to,
    timestamp: unixTimestamp.get(),
    product_slug: productSlug,
    shipping_price: getPricesRange(quotesPrices),
    currency: artaQuoteRequest.currency,
    status: artaQuoteRequest.status,
    disqualifications: getRelevantDisqualifications({
      requestedQuoteTypes: quoteTypes,
      disqualifications: artaQuoteRequest.disqualifications,
    }),
  };
};

/**
 * @description Generates a composite key for the shipping estimate.
 * The purpose is to compare two estimates and see if they are they match,
 * which fits our need and will be more efficient than deep comparing the 2 whole objects.
 * @param {object} estimateSummary - The summary of the shipping estimate.
 * @returns {string} The composite key as a string.
 */
export const getCompositeKey = (
  estimateSummary: Pick<ArtaShippingEstimate, 'from' | 'to' | 'product_slug' | 'currency'>,
): string => {
  return `${estimateSummary.product_slug}_${estimateSummary.from.country}_${estimateSummary.from.city || ''}_${estimateSummary.to.country}_${estimateSummary.to.city || ''}_${estimateSummary.currency}`;
};

/**
 * @description Checks if the cached shipping estimate has the same values as the new one.
 * this will help us determine if need to fetch a new estimate.
 * for example:
 * - if `from` and `to` locations are the same, product is the same and currency is the same,
 * then we can assume the cached estimate is still valid and we don't need to fetch a new one.
 * @param {object} cached - The cached shipping estimate.
 * @param {object} current - The current shipping estimate.
 * @returns {boolean} `true` if the cached estimate is valid, `false` otherwise.
 */
export const isCachedValid = (
  cached: Pick<ArtaShippingEstimate, 'from' | 'to' | 'product_slug' | 'currency'>,
  current: Pick<ArtaShippingEstimate, 'from' | 'to' | 'product_slug' | 'currency'>,
): boolean => {
  return getCompositeKey(cached) === getCompositeKey(current);
};

/**
 * @description Maps our internal currency codes to the supported Arta currency codes.
 * Note: Arta does not support all currencies we use internally,
 * so we map some currencies to the closest supported one.
 * @param {"GBP" | "USD" | "EUR" | "CAD" | "AUD"} afCurrency - The internal currency code.
 * @returns {"GBP" | "USD" | "EUR" | "CAD" | "HKD"} The supported Arta currency code,
 * - without "HKD".
 * - "AUD" is mapped to "USD" as Arta does not support "AUD".
 */
export const getArtaSupportedCurrency = (afCurrency: CurrencyType): ArtaSupportedCurrency => {
  const mapping: Record<CurrencyType, ArtaSupportedCurrency> = {
    GBP: 'GBP',
    USD: 'USD',
    EUR: 'EUR',
    CAD: 'CAD',
    AUD: 'USD',
  };
  return mapping[afCurrency];
};

/**
 * @description Determines the subtype of the Arta object based on the product category and type.
 * @param {string} categorySlug product category slug (e.g., "painting-oil", "sculpture", "photography", etc.)
 * @param {string|undefined} type (optional)product type (e.g., "framed", "unframed", etc.)
 * @returns {string} The `ArtaObjectSubType` determined subtype string of the Arta object.
 */
export const getArtaSubtype = (categorySlug: string, type?: string): ArtaObject['subtype'] => {
  if (
    categorySlug.startsWith('painting') ||
    categorySlug.startsWith('print') ||
    categorySlug.startsWith('drawing')
  ) {
    return type === 'framed' ? 'painting_framed' : 'painting_unframed';
  }
  if (categorySlug.startsWith('sculpture')) {
    return 'sculpture';
  }
  if (categorySlug.startsWith('photography')) {
    return type === 'framed' ? 'photograph_framed' : 'photograph_unframed';
  }

  return 'other';
};

/**
 * @description Constructs an Arta object from the product details.
 * @param params - The parameters object.
 * @param {object} params.product - The product details including pricing and dimensions.
 * @param {"GBP" | "USD" | "EUR" | "CAD" | "AUD"} params.currency - The selected currency code.
 * @param {string} params.categorySlug - The category slug of the product for example `"painting-oil"`.
 * @returns {object} The constructed Arta object as `ArtaObject` type .
 */
export const getArtaObjectFromProduct = ({
  product,
  currency,
  categorySlug,
}: {
  product: Pick<PrintsData, 'pricing' | 'dimensions_text_cm' | 'id'>;
  currency: CurrencyType;
  categorySlug: string;
}): ArtaObject => {
  const artaCurrency = getArtaSupportedCurrency(currency);
  const dimensions = parseDimensionsFromString(product.dimensions_text_cm);

  return {
    value: product.pricing[artaCurrency].current_amount,
    value_currency: artaCurrency,
    width: dimensions?.width || 0,
    height: dimensions?.height || 0,
    depth: dimensions?.depth,
    unit_of_measurement: dimensions?.units,
    subtype: getArtaSubtype(categorySlug, dimensions?.type),
  };
};

/**
 * TODO: remove and replace by origin country and city values provided by
 * API `/api/product/:slug/shipping/` endpoint when available.
 * @description Determines the country code and city name from a given country name.
 * this is needed because Arta API requires country codes and city names for origin and destination.
 * @param {string} countryName
 * @returns {ArtaLocation | null} - Arta location object containing the country code and city name, or `null` if not found.
 */
export const getCountryDataByName = (countryName?: string): ArtaLocation | null => {
  if (!countryName) return null;

  const countryFound = countries.find((country) => country.name === countryName);
  if (!countryFound) {
    return null;
  }

  const {code, capital, city} = countryFound;
  const cityData = {
    name: capital ? capital : city?.name,
    postcode: capital && capital === city?.name ? city.postcode : undefined,
  };

  return {
    country: code,
    city: cityData.name as string,
    postal_code: cityData.postcode,
  };
};

const getOriginCountryName = ({
  productShipping,
  productSlug,
}: {
  productShipping: ProductData['shipping'];
  productSlug: string;
}): string => {
  const shippingOriginal = productShipping.original;
  let countryName = productShipping.original.shipping_from;

  if (!shippingOriginal || !Object.keys(shippingOriginal).length) {
    countryName = productShipping.prints[productSlug]?.shipping_from;
  }

  return countryName;
};

/**
 * TODO: remove and replace by origin country and city values provided by
 * API `/api/product/:slug/shipping/` endpoint when available.
 * @description Get Arta origin location object based on product shipping info
 * @param params - The parameters object.
 * @param {object} params.productShipping - The product shipping information.
 * @param {string} params.productSlug - The product slug, used to access specific print shipping info.
 * @returns {object} - The Arta origin location object of `ArtaLocation` type.
 */
export const getArtaOrigin = ({
  productShipping,
  productSlug,
}: {
  productShipping: ProductData['shipping'];
  productSlug: string;
}): ArtaLocation =>
  getCountryDataByName(getOriginCountryName({productShipping, productSlug})) || {
    country: '',
    city: '',
  };

const isArtaQuoteType = (quoteType: string): quoteType is ArtaQuoteType =>
  quoteType === 'parcel' ||
  quoteType === 'premium' ||
  quoteType === 'select' ||
  quoteType === 'self_ship';

/**
 * @description Maps an optional array of quote types to the supported Arta quote types,
 * and sanitizes out the unsupported ones.
 * @param {string[]} afQuoteTypes - Artfinder provided list of preferred quote types
 * @returns {("parcel" | "premium" | "select" | "self_ship")[] | undefined} - Array of supported Arta quote types or `undefined`
 */
export const getArtaQuoteTypes = (afQuoteTypes?: string[]): ArtaQuoteType[] | undefined => {
  if (!afQuoteTypes || !Array.isArray(afQuoteTypes) || afQuoteTypes.length === 0) {
    return;
  }
  const artaQuoteTypes = afQuoteTypes.map((type) => type.toLowerCase()).filter(isArtaQuoteType);
  return artaQuoteTypes.length > 0 ? artaQuoteTypes : undefined;
};
